import {
	getConfidenceBiasZoneLabel,
	getConfidenceBiasPct,
	getCalibrationLevel,
	CONFIDENCE_BIAS_FAR_ZONE_THRESHOLD,
	CALIBRATION_TONES,
	ConfidenceBiasZoneLabel,
	CalibrationLevel,
} from '@sportspredict/shared';

/**
 * Utility functions for the Performance Summary page.
 */

/**
 * Formats a decimal probability as a rounded percentage string (e.g. 0.15 → "15%").
 */
export const formatPercentage = (value: number): string => `${Math.round(value * 100)}%`;

/** Formats 1 → "1st", 22 → "22nd", 78 → "78th". */
export const formatOrdinal = (value: number): string => {
	const rounded = Math.round(value);
	const suffixes = ['th', 'st', 'nd', 'rd'];
	const remainder = rounded % 100;
	return `${rounded}${suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]}`;
};

/**
 * Formats an RBP value with sign and one decimal (e.g. 36.6 → "+36.6", -28 → "-28.0").
 */
export const formatRbpValue = (rbp: number): string =>
	rbp >= 0 ? `+${rbp.toFixed(2)}` : rbp.toFixed(2);

/**
 * Returns a Tailwind text-color class based on whether RBP is positive or negative.
 */
export const getRbpColorClass = (rbp: number): string =>
	rbp >= 0 ? 'text-[#15803d]' : 'text-[#b91c1c]';

/**
 * Maps a numeric outcome (0 or 1) to a human-readable "YES" / "NO" string.
 */
export const formatOutcome = (outcome: number): string => (outcome >= 0.5 ? 'YES' : 'NO');

/**
 * Formats confidence bias as a signed percentage (e.g. 0.09 → "+9%", -0.05 → "-5%").
 */
export const formatConfidenceBias = (bias: number): string => {
	const rounded = Math.round(bias * 100);
	return rounded >= 0 ? `+${rounded}%` : `${rounded}%`;
};

/**
 * Formats contrarian win rate as a percentage string (e.g. 0.75 → "75%").
 * Returns "-" when null.
 */
export const formatContrarianWinRate = (rate: number | null): string =>
	rate != null ? `${Math.round(rate * 100)}%` : '-';

export interface ConfidenceBiasZone {
	pct: number;
	sign: '+' | '-';
	displayValue: string;
	label: ConfidenceBiasZoneLabel;
	color: string;
	caption: string;
}

export const CONFIDENCE_BIAS_COLORS: Record<ConfidenceBiasZoneLabel, string> = {
	[ConfidenceBiasZoneLabel.Timid]: '#1D4ED8',
	[ConfidenceBiasZoneLabel.Optimal]: '#15803D',
	[ConfidenceBiasZoneLabel.Aggressive]: '#B91C1C',
};

const getConfidenceBiasCaption = (pct: number, label: ConfidenceBiasZoneLabel): string => {
	if (label === ConfidenceBiasZoneLabel.Optimal) {
		return 'Your confidence is well calibrated. Exactly where you want to be.';
	}

	if (label === ConfidenceBiasZoneLabel.Aggressive) {
		return pct > CONFIDENCE_BIAS_FAR_ZONE_THRESHOLD
			? "You're overcommitting! Play it safe for now."
			: 'A bit too bold. Ease off on your least-certain picks.';
	}

	return pct < -CONFIDENCE_BIAS_FAR_ZONE_THRESHOLD
		? "You're playing it too safe. Commit harder or risk wasting easy RBP."
		: 'Too shy? Trust your instincts a little more.';
};

/**
 * Derives the confidence-bias gauge zone (TIMID/OPTIMAL/AGGRESSIVE) from a raw bias decimal.
 */
export const getConfidenceBiasZone = (bias: number): ConfidenceBiasZone => {
	const pct = getConfidenceBiasPct(bias);
	const label = getConfidenceBiasZoneLabel(bias);
	const sign = pct >= 0 ? '+' : '-';

	return {
		pct,
		sign,
		displayValue: `${sign}${Math.abs(Math.round(pct))}%`,
		label,
		color: CONFIDENCE_BIAS_COLORS[label],
		caption: getConfidenceBiasCaption(pct, label),
	};
};

export interface CalibrationInterpretation {
	tone: string;
	level: CalibrationLevel;
	color: string;
}

const CALIBRATION_LEVEL_COLORS: Record<CalibrationLevel, string> = {
	[CalibrationLevel.Elite]: '#15803D',
	[CalibrationLevel.Good]: '#3b4a78',
	[CalibrationLevel.Developing]: '#B91C1C',
	[CalibrationLevel.Insufficient]: '#848484',
};

/**
 * Derives a calibration-quality tone from bins.
 */
export const getCalibrationInterpretation = (
	bins: { n: number; hitRate: number | null; avgPredicted: number | null }[]
): CalibrationInterpretation => {
	const level = getCalibrationLevel(bins);

	return {
		tone: CALIBRATION_TONES[level],
		level,
		color: CALIBRATION_LEVEL_COLORS[level],
	};
};
